import { useLayoutEffect, useState } from "react";

import useMantineMediaQueries from "@hl/base-components/lib/hooks/useMantineMediaQueries";
import MantineProviderStatic from "@hl/base-components/lib/theme/MantineProviderStatic";
import { SECONDARY_COLOR } from "@hl/base-components/lib/theme/button";
import { ResizedImage } from "@hl/shared-features/lib/features/image";
import DescriptionMarkdown from "@hl/shared-features/lib/features/markdown/DescriptionMarkdown";
import HighlightedNotes from "@hl/shared-features/lib/features/markdown/HighlightedNotes";
import { WatchlistButton } from "@hl/shared-features/lib/features/watchlist-and-follow/watchlist/WatchlistButton";
import {
  Button,
  Flex,
  Group,
  ScrollArea,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from "@mantine/core";

import { TOOLS_URL } from "~config";
import CollectionCreatedBy from "~features/MintPage/CollectionCreatedBy";
import CollectionInfoHeaderMenu from "~features/MintPage/apollo/CollectionInfoHeader/CollectionInfoHeaderMenu";
import MarketplaceStatsHeader from "~features/MintPage/apollo/MarketplaceStatsHeader";
import { TagsCategoriesDetails } from "~features/MintPage/components/DetailSection/tags-categories/TagsCategoriesDetails";
import useMintState from "~hooks/useMintState";

import { SpamWarning } from "../../copy";

type CardFooterProps = {
  imageUrl?: string | null;
};

async function extractFirstGifFrame(gifUrl: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous"; // Handle cross-origin issues
    img.src = gifUrl;

    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");

      if (ctx) {
        ctx.drawImage(img, 0, 0); // Draw the first frame on the canvas
        resolve(canvas.toDataURL("image/png")); // Convert to a static PNG
      } else {
        reject("Failed to create canvas context.");
      }
    };

    img.onerror = () => reject("Failed to load GIF.");
  });
}

async function isAnimatedImageByContentType(
  url?: string | null
): Promise<boolean> {
  if (!url) return false;
  try {
    const res = await fetch(url, { method: "HEAD" });
    const contentType = res.headers.get("Content-Type") || "";

    if (contentType.includes("image/gif")) return true;

    if (contentType.includes("image/webp")) return true;

    return false;
  } catch (err) {
    console.error("Error fetching banner:", err);
    return false;
  }
}

const CollectionInfoHeader = ({ imageUrl }: CardFooterProps) => {
  const { collection, walletAddress } = useMintState();
  const title = collection?.name ?? "";
  const theme = useMantineTheme();

  const { isMedium, isMobile, isLarge } = useMantineMediaQueries({
    withInitialValues: true,
  });
  const [staticBannerImage, setStaticBannerImage] = useState<string | null>(
    null
  );
  const [animated, setAnimated] = useState<boolean>(false);

  const bannerImageUrl = collection?.bannerImageUrl || imageUrl;
  const showBannerImage = !!staticBannerImage || !!bannerImageUrl;

  useLayoutEffect(() => {
    isAnimatedImageByContentType(bannerImageUrl).then((isAnimated) => {
      if (isAnimated && bannerImageUrl) {
        extractFirstGifFrame(bannerImageUrl).then((frame) => {
          setStaticBannerImage(frame);
          setAnimated(true);
        });
      } else {
        setStaticBannerImage(bannerImageUrl || null);
        setAnimated(false);
      }
    });
  }, [bannerImageUrl]);

  const finalBannerUrl = animated ? staticBannerImage : bannerImageUrl;

  const detailsAndOwner = (
    <Flex align="center" gap={8} justify="flex-end">
      <Group align="center">
        {collection?.creatorAddresses?.some(
          (evmAddress) => evmAddress.address === walletAddress
        ) && (
          <>
            <Flex gap={8} justify="flex-end" sx={{ flexWrap: "wrap" }}>
              {!collection?.bannerImageUrl && (
                <Button
                  component="a"
                  size="xs"
                  href={`${TOOLS_URL.collections.manage.replace(
                    ":collectionId",
                    collection?.id ?? ""
                  )}?view=banner`}
                >
                  Add banner
                </Button>
              )}
              <Button
                component="a"
                color={SECONDARY_COLOR}
                size="xs"
                href={TOOLS_URL.collections.manage.replace(
                  ":collectionId",
                  collection.id
                )}
              >
                Manage collection
              </Button>
            </Flex>
          </>
        )}
      </Group>
      <MantineProviderStatic type={theme.colorScheme}>
        <CollectionInfoHeaderMenu hasBanner={showBannerImage} />
      </MantineProviderStatic>
    </Flex>
  );

  return (
    <Stack
      spacing={0}
      pt={40}
      sx={{
        borderBottom: `1px solid ${theme.colors.divider[0]}`,
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          inset: -40,
          background: showBannerImage
            ? `center / cover no-repeat url("${finalBannerUrl}")`
            : theme.colors.primaryBackground[0],
          filter: collection?.bannerImageUrl ? undefined : "blur(50px)",
        },
      }}
      mx={isMobile ? -16 : -40}
    >
      {showBannerImage && (
        <>
          <Flex
            w="100%"
            h="100%"
            sx={{
              position: "absolute",
              bottom: 0,
              zIndex: 1,
              background: collection?.bannerImageUrl
                ? "linear-gradient(180deg, rgba(0,0,0,0.15228028711484589) 0%, rgba(0,0,0,0.371187850140056) 15%, rgba(0,0,0,0.54365283613445376) 30%, rgba(0,0,0,0.6709077380952381) 46%, rgba(0,0,0,0.7149413515406162) 68%, rgba(0,0,0,0.7193391106442577) 100%)"
                : "linear-gradient(180deg, rgb(0% 0% 0% / 0.05) 0%, rgb(0% 0% 0% / 0.08284047889111632) 6.25%, rgb(0% 0% 0% / 0.12093312855939615) 12.5%, rgb(0% 0% 0% / 0.14634585661680007) 18.75%, rgb(0% 0% 0% / 0.18054563517369942) 25%, rgb(0% 0% 0% / 0.22221818591960938) 31.25%, rgb(0% 0% 0% / 0.2797620560996003) 37.5%, rgb(0% 0% 0% / 0.34135016144556474) 43.75%, rgb(0% 0% 0% / 0.39199999999999997) 50%, rgb(0% 0% 0% / 0.4446498385544353) 56.25%, rgb(0% 0% 0% / 0.49523794390039967) 62.5%, rgb(0% 0% 0% / 0.52778181408039057) 68.75%, rgb(0% 0% 0% / 0.5694543648263006) 75%, rgb(0% 0% 0% / 0.6036541433832) 81.25%, rgb(0% 0% 0% / 0.6290668714406039) 87.5%, rgb(0% 0% 0% / 0.6447159521108884) 93.75%, rgb(0% 0% 0% / 0.65) 100% )",
            }}
          ></Flex>
        </>
      )}
      <Flex
        pb={isMobile ? 16 : 32}
        sx={{ zIndex: 1 }}
        pt={!showBannerImage ? (isMobile ? 16 : 32) : undefined}
      >
        <MantineProviderStatic type="dark" isEnabled={showBannerImage}>
          <Flex
            justify="space-between"
            px={isMedium ? 40 : 20}
            w="100%"
            gap={16}
          >
            <Stack
              spacing={16}
              justify="flex-end"
              w={isLarge ? undefined : "100%"}
              sx={{ flex: isLarge ? 5 : undefined }}
            >
              <Flex justify="space-between">
                <Flex
                  gap="xs"
                  direction={isMobile ? "column" : "row"}
                  align={isMobile ? "start" : "center"}
                  w={isMobile ? "100%" : undefined}
                  pt={isMobile ? 0 : 60}
                >
                  <Flex
                    justify="space-between"
                    w={isMobile ? "100%" : undefined}
                  >
                    <ResizedImage
                      styles={{
                        root: {
                          width: "unset !important",
                        },
                      }}
                      src={imageUrl}
                      fit="contain"
                      height={isMobile ? 60 : 80}
                    />
                    {isMobile && detailsAndOwner}
                  </Flex>
                  <Stack spacing={0} maw={600}>
                    <Group>
                      <Title lineClamp={1} order={isMobile ? 6 : 5}>
                        {title}
                      </Title>
                      <Text>
                        <WatchlistButton onchainId={collection?.onchainId} />
                      </Text>
                    </Group>

                    <Flex justify="space-between" align="center" gap="xs">
                      <Text size={isMedium ? "xs" : 10}>
                        <CollectionCreatedBy numLines="1" />
                      </Text>
                    </Flex>
                  </Stack>
                </Flex>
                {!isMobile && !isLarge && (
                  <Flex align="flex-end">{detailsAndOwner}</Flex>
                )}
              </Flex>
              <DescriptionMarkdown
                description={collection?.description || ""}
                hasExpand={!collection?.flagVariations.increasedSidebarWidth}
                lineClamp={isMobile ? 3 : 1}
                numChars={300}
                hasLineClampExpanded={false}
              />
              <Flex justify="space-between" align="flex-end">
                <ScrollArea
                  type="never"
                  sx={{
                    marginLeft: isMobile ? -20 : -40,
                    marginRight: isMobile ? -20 : -40,
                  }}
                  styles={{
                    viewport: {
                      paddingLeft: isMobile ? 20 : 40,
                      paddingRight: isMobile ? 20 : 40,
                    },
                  }}
                >
                  <Stack>
                    <MarketplaceStatsHeader />
                    {collection?.id && (
                      <TagsCategoriesDetails
                        collectionId={collection?.id}
                        compact
                      />
                    )}
                  </Stack>
                </ScrollArea>
              </Flex>
              {collection?.mintWarning && !isLarge && (
                <SpamWarning mintWarning={collection.mintWarning} />
              )}
              {collection?.editorial && !isLarge && (
                <HighlightedNotes
                  editorial={collection?.editorial}
                  numChars={256}
                />
              )}
            </Stack>
            {isLarge && (
              <Stack
                spacing={16}
                align="flex-end"
                justify="flex-end"
                sx={{ flex: 2, maxWidth: 400 }}
              >
                {collection?.mintWarning && (
                  <SpamWarning mintWarning={collection.mintWarning} />
                )}
                {collection?.editorial && isLarge && (
                  <HighlightedNotes
                    editorial={collection?.editorial}
                    numChars={192}
                  />
                )}
                {detailsAndOwner}
              </Stack>
            )}
          </Flex>
        </MantineProviderStatic>
      </Flex>
    </Stack>
  );
};

export default CollectionInfoHeader;
